import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import type { ACTION } from '@atlaskit/editor-common/analytics/types/enums';
import type { CardReplacementInputMethod } from '@atlaskit/editor-common/card/types';
import type {
	CardAppearance,
	CardProvider,
} from '@atlaskit/editor-common/provider-factory/card-provider';
import type { DatasourceModalType } from '@atlaskit/editor-common/types/datasource';
import type { EditorAppearance } from '@atlaskit/editor-common/types/editor-appearance';
import type {
	EditorState,
	ReadonlyTransaction,
	Transaction,
} from '@atlaskit/editor-prosemirror/state';
import type { DatasourceAdfView } from '@atlaskit/linking-common/types';
import type { SmartLinkEvents } from '@atlaskit/smart-card/smart-link-events';

import type {
	CardPluginState,
	Request,
	ToolbarResolvedAttributes,
	PastedInlineLink,
} from '../../types';
import type { DatasourceTableLayout } from '../../ui/LayoutButton/types';
import { pluginKey } from '../plugin-key';
import { mapPastedInlineLinks } from './pasted-inline-link';

// ============================================================================ //
// ============================== PLUGIN STATE ================================ //
// ============================================================================ //
// Used for interactions with the Card Plugin's state.
// ============================================================================ //
export const getPluginState = (editorState: EditorState) =>
	pluginKey.getState(editorState) as CardPluginState | undefined;

export const getPluginStateWithUpdatedPos = (
	pluginState: CardPluginState,
	tr: ReadonlyTransaction | Transaction,
): {
	allowBlockCards?: boolean;
	allowEmbeds?: boolean;
	cards: {
		id?: string;
		pos: number;
		title?: string;
		url?: string;
	}[];
	datasourceModalType?: DatasourceModalType;
	datasourceStash: {
		[x: string]: {
			views: DatasourceAdfView[];
		};
	};
	datasourceTableRef?: HTMLElement;
	editorAppearance?: EditorAppearance;
	inlineCardAwarenessCandidatePosition?: number;
	layout?: DatasourceTableLayout;
	overlayCandidatePosition?: number;
	pastedInlineLinks?: PastedInlineLink[];
	pastedInlineLinkShown?: boolean;
	provider: CardProvider | null;
	removeOverlay?: () => void;
	removeOverlayCardId?: string;
	requests: {
		analyticsAction?: ACTION;
		appearance: CardAppearance;
		compareLinkText: boolean;
		pos: number;
		previousAppearance?: CardAppearance | 'url';
		shouldReplaceLink?: boolean;
		source: CardReplacementInputMethod;
		sourceEvent?: UIAnalyticsEvent | null | undefined;
		url: string;
	}[];
	resolvedInlineSmartLinks?: Array<{
		pos: number;
		source: CardReplacementInputMethod;
		url: string;
	}>;
	resolvedToolbarAttributesByUrl: Record<string, ToolbarResolvedAttributes>;
	selectedInlineLinkPosition?: number;
	showDatasourceModal: boolean;
	showLinkingToolbar: boolean;
	smartLinkEvents?: SmartLinkEvents;
} => ({
	...pluginState,
	...(pluginState.pastedInlineLinks
		? { pastedInlineLinks: mapPastedInlineLinks(pluginState.pastedInlineLinks, tr) }
		: {}),
	requests: pluginState.requests.map((request) => ({
		...request,
		pos: tr.mapping.map(request.pos),
	})),
	cards: pluginState.cards.map((card) => ({
		...card,
		pos: tr.mapping.map(card.pos),
	})),
	resolvedInlineSmartLinks: pluginState.resolvedInlineSmartLinks?.map((card) => ({
		...card,
		pos: tr.mapping.map(card.pos),
	})),
});

export const getNewRequests = (
	oldState: CardPluginState | undefined,
	currentState: CardPluginState,
): Request[] => {
	if (oldState) {
		return currentState.requests.filter(
			(req) => !oldState.requests.find((oldReq) => isSameRequest(oldReq, req)),
		);
	}
	return currentState.requests;
};

const isSameRequest = (requestA: Request, requestB: Request) =>
	requestA.url === requestB.url && requestA.pos === requestB.pos;
